import { type SchoolSignInDto } from "@crowdcoursing/api/openapi";
import { Close, Search } from "@mui/icons-material";
import { Box, Divider, Drawer, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";

import { SchoolSignInCard } from "~ui/molecules/SchoolSignInCard/SchoolSignInCard";

const tTitle = "Sign-ins";
const tOnDay = "On";
const tFullRange = "Selected date range";
const tSearch = "Search by name or email…";
const tNoSignIns = "No sign-ins for this view.";
const tNoMatches = "No sign-ins match your search.";

function signInDisplayName(row: SchoolSignInDto): string {
  const last = row.lastName?.trim() ?? "";
  const first = row.firstName?.trim() ?? "";
  if (last && first) {
    return `${last}, ${first}`;
  }
  if (last) {
    return last;
  }
  if (first) {
    return first;
  }
  return "—";
}

function matchesSearch(row: SchoolSignInDto, q: string): boolean {
  if (!q.trim()) {
    return true;
  }
  const needle = q.trim().toLowerCase();
  return (
    signInDisplayName(row).toLowerCase().includes(needle) ||
    (row.email ?? "").toLowerCase().includes(needle) ||
    (row.phoneNumber ?? "").toLowerCase().includes(needle)
  );
}

export interface SchoolSignInsTemplateProps {
  open: boolean;
  onClose: () => void;
  /** When set, subtitle shows this calendar day; when null, entire range. */
  filterDayKey: string | null;
  signIns: SchoolSignInDto[];
}

export function SchoolSignInsTemplate({ open, onClose, filterDayKey, signIns }: SchoolSignInsTemplateProps) {
  const [searchText, setSearchText] = useState("");

  const subtitle = useMemo(() => {
    if (filterDayKey) {
      const dt = DateTime.fromFormat(filterDayKey, "yyyy-MM-dd");
      return `${tOnDay} ${dt.toLocaleString(DateTime.DATE_FULL)}`;
    }
    return tFullRange;
  }, [filterDayKey]);

  const filtered = useMemo(() => signIns.filter((row) => matchesSearch(row, searchText)), [searchText, signIns]);

  return (
    <Drawer
      anchor="right"
      onClose={onClose}
      open={open}
      slotProps={{
        paper: {
          sx: { width: { sm: 420, xs: "100%" } },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ alignItems: "center", display: "flex", justifyContent: "space-between", p: 2 }}>
          <Box sx={{ minWidth: 0, pr: 1 }}>
            <Typography variant="h6">
              {tTitle}
              {signIns.length > 0 ? ` (${signIns.length})` : ""}
            </Typography>
            <Typography color="text.secondary" variant="subtitle2">
              {subtitle}
            </Typography>
          </Box>
          <IconButton aria-label="Close sign-ins" onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
        <Divider />

        <Box sx={{ p: 2, pb: 0 }}>
          <TextField
            fullWidth
            placeholder={tSearch}
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {signIns.length === 0 ? (
            <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
              {tNoSignIns}
            </Typography>
          ) : filtered.length === 0 ? (
            <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
              {tNoMatches}
            </Typography>
          ) : (
            <Box display="grid" gap={2}>
              {filtered.map((row, index) => (
                <SchoolSignInCard
                  key={`${row.userId ?? "u"}-${row.timestamp ?? index}`}
                  displayName={signInDisplayName(row)}
                  email={row.email}
                  phoneNumber={row.phoneNumber}
                  timestamp={row.timestamp}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
